import { motion } from "framer-motion";

const Loader = ({
  size = "md",
  color = "green",
  text = "",
  fullScreen = false,
  className = "",
}) => {
  const sizeClasses = {
    xs: "w-12 h-12",
    sm: "w-16 h-16",
    md: "w-20 h-20",
    lg: "w-24 h-24",
    xl: "w-28 h-28",
  };

  const colorClasses = {
    blue: "text-blue-600",
    green: "text-green-600",
    red: "text-red-600",
    yellow: "text-yellow-600",
    purple: "text-purple-600",
    gray: "text-gray-600",
    white: "text-white",
  };

  const textSizeClasses = {
    xs: "text-xs",
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
    xl: "text-xl",
  };

  const textColorClasses = {
    blue: "text-blue-600",
    green: "text-green-600",
    red: "text-red-600",
    yellow: "text-yellow-600",
    purple: "text-purple-600",
    gray: "text-gray-600",
    white: "text-white",
  };

  const spinner = (
    <div className={`relative ${sizeClasses[size]} ${className}`}>
      {/* Glow background effect */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.3, 0.1],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute inset-0 bg-green-500 rounded-full blur-md"
      />

      {/* Outer rotating ring with enhanced styling */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 border-2 border-t-transparent border-r-transparent rounded-full shadow-lg"
        style={{
          borderColor: colorClasses[color].replace("text-", "border-"),
          filter: "drop-shadow(0 0 3px rgba(34, 197, 94, 0.5))",
        }}
      />

      {/* Inner rotating ring (opposite direction) */}
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        className="absolute inset-2 border border-t-transparent border-l-transparent rounded-full opacity-70"
        style={{
          borderColor: colorClasses[color].replace("text-", "border-"),
          filter: "drop-shadow(0 0 2px rgba(34, 197, 94, 0.3))",
        }}
      />

      {/* Middle dotted ring */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        className="absolute inset-4 border border-dashed border-green-400 rounded-full opacity-40"
      />

      {/* Central shield icon with enhanced glow */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.8, 1, 0.8],
            rotate: [0, 5, -5, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className={`${colorClasses[color]} ${
            size === "xs"
              ? "w-3 h-3"
              : size === "sm"
              ? "w-4 h-4"
              : size === "md"
              ? "w-5 h-5"
              : size === "lg"
              ? "w-6 h-6"
              : "w-8 h-8"
          }`}
          style={{ filter: "drop-shadow(0 0 4px rgba(34, 197, 94, 0.6))" }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-full h-full"
          >
            <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11H16V16H8V11H9.2V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.4,8.7 10.4,10V11H13.6V10C13.6,8.7 12.8,8.2 12,8.2Z" />
          </svg>
        </motion.div>
      </div>

      {/* Enhanced floating document icons with checkmarks */}
      <motion.div
        animate={{
          rotate: 360,
          scale: [0.8, 1.1, 0.8],
          y: [0, -2, 0],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0,
        }}
        className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1"
      >
        <div
          className={`${colorClasses[color]} ${
            size === "xs"
              ? "w-2 h-2"
              : size === "sm"
              ? "w-3 h-3"
              : size === "md"
              ? "w-4 h-4"
              : size === "lg"
              ? "w-5 h-5"
              : "w-6 h-6"
          } opacity-70`}
          style={{ filter: "drop-shadow(0 0 2px rgba(34, 197, 94, 0.4))" }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-full h-full"
          >
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10,12L8,14L11,17L16,12L14.5,10.5L11,14L10,12Z" />
          </svg>
        </div>
      </motion.div>

      <motion.div
        animate={{
          rotate: -360,
          scale: [0.8, 1.1, 0.8],
          y: [0, 2, 0],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5,
        }}
        className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1"
      >
        <div
          className={`${colorClasses[color]} ${
            size === "xs"
              ? "w-2 h-2"
              : size === "sm"
              ? "w-3 h-3"
              : size === "md"
              ? "w-4 h-4"
              : size === "lg"
              ? "w-5 h-5"
              : "w-6 h-6"
          } opacity-70`}
          style={{ filter: "drop-shadow(0 0 2px rgba(34, 197, 94, 0.4))" }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-full h-full"
          >
            <path d="M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M19,3H18V1H16V3H8V1H6V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19Z" />
          </svg>
        </div>
      </motion.div>

      <motion.div
        animate={{
          rotate: 360,
          scale: [0.8, 1.1, 0.8],
          x: [-2, 0, -2],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2"
      >
        <div
          className={`${colorClasses[color]} ${
            size === "xs"
              ? "w-2 h-2"
              : size === "sm"
              ? "w-3 h-3"
              : size === "md"
              ? "w-4 h-4"
              : size === "lg"
              ? "w-5 h-5"
              : "w-6 h-6"
          } opacity-70`}
          style={{ filter: "drop-shadow(0 0 2px rgba(34, 197, 94, 0.4))" }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-full h-full"
          >
            <path d="M17,9H7V7H17M17,13H7V11H17M14,17H7V15H14M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3Z" />
          </svg>
        </div>
      </motion.div>

      <motion.div
        animate={{
          rotate: -360,
          scale: [0.8, 1.1, 0.8],
          x: [2, 0, 2],
        }}
        transition={{
          duration: 2.8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.5,
        }}
        className="absolute right-0 top-1/2 transform translate-x-1 -translate-y-1/2"
      >
        <div
          className={`${colorClasses[color]} ${
            size === "xs"
              ? "w-2 h-2"
              : size === "sm"
              ? "w-3 h-3"
              : size === "md"
              ? "w-4 h-4"
              : size === "lg"
              ? "w-5 h-5"
              : "w-6 h-6"
          } opacity-70`}
          style={{ filter: "drop-shadow(0 0 2px rgba(34, 197, 94, 0.4))" }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-full h-full"
          >
            <path d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.45,13.97L5.82,21L12,17.27Z" />
          </svg>
        </div>
      </motion.div>

      {/* Floating security badges */}
      <motion.div
        animate={{
          rotate: [0, 360],
          scale: [0.6, 0.8, 0.6],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "linear",
          delay: 2,
        }}
        className="absolute top-1/4 right-1/4 transform translate-x-2 -translate-y-2"
      >
        <div
          className={`${colorClasses[color]} ${
            size === "xs"
              ? "w-1.5 h-1.5"
              : size === "sm"
              ? "w-2 h-2"
              : size === "md"
              ? "w-3 h-3"
              : size === "lg"
              ? "w-4 h-4"
              : "w-5 h-5"
          } opacity-50`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-full h-full"
          >
            <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
          </svg>
        </div>
      </motion.div>

      <motion.div
        animate={{
          rotate: [360, 0],
          scale: [0.6, 0.8, 0.6],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "linear",
          delay: 3,
        }}
        className="absolute bottom-1/4 left-1/4 transform -translate-x-2 translate-y-2"
      >
        <div
          className={`${colorClasses[color]} ${
            size === "xs"
              ? "w-1.5 h-1.5"
              : size === "sm"
              ? "w-2 h-2"
              : size === "md"
              ? "w-3 h-3"
              : size === "lg"
              ? "w-4 h-4"
              : "w-5 h-5"
          } opacity-50`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-full h-full"
          >
            <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
          </svg>
        </div>
      </motion.div>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-90 backdrop-blur-sm">
        <div className="flex flex-col items-center justify-center space-y-4">
          {spinner}
          {text && (
            <p
              className={`${textSizeClasses[size]} ${textColorClasses[color]} font-medium`}
            >
              {text}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (text) {
    return (
      <div className="flex items-center justify-center space-x-3">
        {spinner}
        <span
          className={`${textSizeClasses[size]} ${textColorClasses[color]} font-medium`}
        >
          {text}
        </span>
      </div>
    );
  }

  return spinner;
};

export default Loader;
